import { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Button, BUTTON_SIZES } from 'ui/button';
import { InputText } from 'ui/inputs';
import { Modal, ModalFooter, ModalHeader } from 'ui/modal';
import { zIndex } from 'ui/theme';
import { Select } from 'ui/selectv2/Select';
import { isEmptyString } from 'utils/stringHelpers';
import { isDefined } from 'utils/tsHelpers';

import { StyledInputWrapper, StyledModalBody } from './Styled';
import { useExportToFBCAMutation, useListId, useTranslation } from '../../hooks';
import { useModalsContext } from '../../contexts/modals';
import { useFacebookAdAccounts } from '../../contexts/facebookAdAccounts';
import { useSegmentationQueryContext } from '../../contexts/segmentationQuery';
import { ExportToFBCAParams } from '../../types';

const StyledModalBodyOverflow = styled(StyledModalBody)`
  overflow-y: visible;
  z-index: ${zIndex.newDropdown};
`;

export const testIds = {
  modal: 'segmentation-export-to-fbca',
  audienceNameInput: 'segmentation-export-to-fbca-name-input',
  audienceDescInput: 'segmentation-export-to-fbca-desc-input',
};
export const ExportToFBCAModal = () => {
  const { t } = useTranslation();

  const [accountId, setAccountId] = useState<string>();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const { searchQuery } = useSegmentationQueryContext();
  const listId = useListId();

  const { hideModal: closeModal } = useModalsContext();
  const { fbAdAccounts = [] } = useFacebookAdAccounts();

  const { mutate: exportToFBCA } = useExportToFBCAMutation();

  const options = fbAdAccounts.map((account) => {
    const { adAccountId } = account;
    return { label: adAccountId, value: adAccountId };
  });

  const handleExport = () => {
    const [adAccount] = fbAdAccounts.filter((fbAccount) => fbAccount.adAccountId === accountId);
    const params: ExportToFBCAParams = {
      customAudienceName: name,
      customAudienceDescription: description,
      adAccount,
      searchQuery,
    };
    if (listId) {
      params.emailListId = listId;
    }
    exportToFBCA(params);
  };

  const isExportDisabled = useMemo(
    () => isEmptyString(name) || !isDefined(accountId),
    [name, accountId],
  );

  return (
    <Modal id={testIds.modal} onClose={closeModal} isModalOpen>
      <ModalHeader>{t('modal.exportToFBCA.header')}</ModalHeader>
      <StyledModalBodyOverflow>
        <Select
          options={options}
          placeholder={t('modal.exportToFBCA.accountIdPlaceholder')}
          value={[accountId ? { label: accountId, value: accountId } : accountId]}
          onSelect={([selectedAccount]) => setAccountId(selectedAccount.value)}
        />
        <StyledInputWrapper>
          <InputText
            placeholder={t('modal.exportToFBCA.namePlaceholder')}
            value={name}
            onChange={setName}
            data-test={testIds.audienceNameInput}
            isRequired
          />
          <InputText
            placeholder={t('modal.exportToFBCA.descriptionPlaceholder')}
            value={description}
            onChange={setDescription}
            data-test={testIds.audienceDescInput}
          />
        </StyledInputWrapper>
      </StyledModalBodyOverflow>
      <ModalFooter>
        <Button onClick={handleExport} size={BUTTON_SIZES.LARGE} isDisabled={isExportDisabled}>
          {t('modal.exportToFBCA.button')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
